import { AlertCircle, ArrowRight, CheckCircle2, Circle, Clock, Sparkles } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useOpinions } from "@/lib/useOpinions";
import { useTopics } from "@/lib/usePreferences";
import type { TopicCard, TopicStatus } from "@/lib/types";
import { cn } from "@/lib/utils";

// Per-status badge — mirrors the Systems status-pill restraint. "done" gets the
// blue accent; the rest stay muted so the eye lands on what's live.
const STATUS_META: Record<TopicStatus, { label: string; className: string; Icon: typeof Circle }> = {
  not_started: { label: "Not started", className: "bg-bw-blue-muted/20 text-bw-ink-muted", Icon: Circle },
  in_progress: { label: "In progress", className: "bg-bw-blue-muted/20 text-bw-navy", Icon: Clock },
  done: { label: "Done", className: "bg-bw-blue/10 text-bw-navy", Icon: CheckCircle2 },
};

/**
 * How the catalog is laid out: by preference group, or as one list in the
 * person's own order. Recommended is the default; the ask is "sit your ten",
 * not "browse seventy-seven".
 */
type SortMode = "group" | "recommended";
const SORT_STORAGE_KEY = "northstar.surveys.sort";

function readSortMode(): SortMode {
  try {
    return window.localStorage.getItem(SORT_STORAGE_KEY) === "group" ? "group" : "recommended";
  } catch {
    return "recommended";
  }
}

function writeSortMode(mode: SortMode) {
  try {
    window.localStorage.setItem(SORT_STORAGE_KEY, mode);
  } catch {
    // A remembered view is a convenience; private windows may refuse storage.
  }
}

/** How many ranked, not-done topics the strip shows. Three is a nudge; ten is a list. */
const RECOMMENDED_COUNT = 3;

/**
 * How many of a commit ranking count as "recommended". The seed ranks every
 * topic an engineer has any commit evidence for (up to ~70), so the rest of
 * the ranking still orders the next-topic nudge, but the ask is "sit your
 * ten". Role and default lists are short already and are never cut.
 */
const RECOMMENDED_CAP = 10;

function isRecommended(t: TopicCard): boolean {
  if (t.recommended_rank == null) return false;
  return t.recommendation_track !== "commits" || t.recommended_rank <= RECOMMENDED_CAP;
}

/**
 * The person's next best surveys, from their own ranking (commit history × the
 * repo survey for engineers, a role map for everyone else). Rendered only when
 * the API ranked something for them; the `default` track is the set served to
 * anyone the roster doesn't name, and is worded as a starting point rather
 * than as a claim about their work.
 */
function RecommendedStrip({ topics }: { topics: TopicCard[] }) {
  const ranked = topics
    .filter((t) => t.status !== "done" && t.recommended_rank != null)
    .sort((a, b) => (a.recommended_rank ?? 0) - (b.recommended_rank ?? 0))
    .slice(0, RECOMMENDED_COUNT);
  if (ranked.length === 0) return null;
  // One track per person in practice; the first ranked card says which.
  const track = ranked[0].recommendation_track;
  const isDefault = track === "default";
  const subtitle =
    track === "commits"
      ? "Ordered by the code you've committed to in the last six months."
      : track === "role"
        ? "The decisions your role is closest to."
        : "The topics most engineers rank highly. Your own ranking appears once you're on the roster.";
  return (
    <section aria-labelledby="recommended-heading" className="flex flex-col gap-3">
      <div className="flex items-center gap-2">
        <Sparkles className="h-4 w-4 text-bw-blue" aria-hidden />
        <h2 id="recommended-heading" className="font-sans text-sm font-semibold uppercase tracking-wide text-bw-navy">
          {isDefault ? "Start here" : "Recommended for you"}
        </h2>
        <span className="font-sans text-xs text-bw-ink-muted">{subtitle}</span>
      </div>
      <ol className="grid gap-3 sm:grid-cols-3">
        {ranked.map((t) => (
          <li key={t.key}>
            <Link
              to={`/preferences/${t.key}`}
              className="flex h-full flex-col gap-1 rounded-card border border-bw-blue/40 bg-bw-blue/5 p-4 transition-colors hover:border-bw-blue focus:outline-none focus-visible:ring-2 focus-visible:ring-bw-blue"
            >
              <span className="font-sans text-xs font-semibold text-bw-blue">#{t.recommended_rank} for you</span>
              <span className="flex items-center gap-1 font-semibold text-bw-navy">
                {t.title}
                <ArrowRight className="h-3.5 w-3.5" aria-hidden />
              </span>
              {/* Only a commit ranking has a per-topic reason worth reading. A role
                  or default basis is the same line on every card, and the
                  subtitle already says it. */}
              {t.recommendation_track === "commits" && t.recommended_because && (
                <span className="font-sans text-xs text-bw-ink-muted">You commit to {t.recommended_because}</span>
              )}
            </Link>
          </li>
        ))}
      </ol>
    </section>
  );
}

function TopicCardView({ topic, positionCount }: { topic: TopicCard; positionCount?: number }) {
  const meta = STATUS_META[topic.status];
  return (
    <Link
      to={`/preferences/${topic.key}`}
      className="block rounded-card focus:outline-none focus-visible:ring-2 focus-visible:ring-bw-blue focus-visible:ring-offset-2"
    >
      <Card className="flex h-full flex-col transition-colors hover:border-bw-blue">
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between gap-3">
            <CardTitle>{topic.title}</CardTitle>
            <span className="flex shrink-0 items-center gap-1.5">
              {topic.recommended_rank != null && topic.status !== "done" && (
                <span className="rounded-pill bg-bw-blue/10 px-2 py-1 font-sans text-xs font-semibold text-bw-blue">
                  #{topic.recommended_rank}
                </span>
              )}
              <span
                className={cn(
                  "flex shrink-0 items-center gap-1 rounded-pill px-3 py-1 font-sans text-xs font-semibold",
                  meta.className,
                )}
              >
                <meta.Icon className="h-3.5 w-3.5" aria-hidden />
                {meta.label}
              </span>
            </span>
          </div>
        </CardHeader>
        <CardContent className="flex flex-1 flex-col gap-3 pt-0">
          {/* Who is using the AI in this topic. A card that says "model" or
              "AI" without saying whose reads as three different topics; the
              frame line settles it before the prose. Unframed topics show
              nothing — the line would be noise on sixty cards. */}
          {topic.frame && topic.frame !== "none" && topic.frame_label && (
            <p className="font-sans text-xs font-semibold uppercase tracking-wide text-bw-blue">
              About: {topic.frame_label}
            </p>
          )}
          <p className="font-sans text-sm text-bw-ink-muted">{topic.prose}</p>

          {topic.current_stack_tags.length > 0 ? (
            <div className="flex flex-wrap items-center gap-1.5">
              <span className="font-sans text-xs text-bw-ink-muted">Your stack today:</span>
              {topic.current_stack_tags.map((tag) => (
                <span
                  key={tag}
                  className="rounded-pill bg-bw-blue/10 px-2.5 py-0.5 font-sans text-xs font-semibold text-bw-navy"
                >
                  {tag}
                </span>
              ))}
            </div>
          ) : (
            <span className="font-sans text-xs italic text-bw-ink-muted">No standard yet</span>
          )}

          <div className="mt-auto flex items-center justify-between gap-3 pt-2 font-sans text-xs text-bw-ink-muted">
            <span>
              {topic.decision_point_count} decision point
              {topic.decision_point_count === 1 ? "" : "s"}
            </span>
            {/* Live from /api/opinions; undefined while loading/unavailable. */}
            {positionCount !== undefined && (
              <span className="rounded-pill bg-muted px-2.5 py-0.5">
                {positionCount > 0
                  ? `Team position: set (${positionCount} of ${topic.decision_point_count})`
                  : "Team position: not set"}
              </span>
            )}
          </div>
        </CardContent>
      </Card>
    </Link>
  );
}

function SortToggle({
  mode,
  onChange,
  isDefault,
}: {
  mode: SortMode;
  onChange: (m: SortMode) => void;
  isDefault: boolean;
}) {
  const options: { value: SortMode; label: string }[] = [
    { value: "group", label: "By group" },
    { value: "recommended", label: isDefault ? "Start here" : "Recommended for me" },
  ];
  return (
    <div role="group" aria-label="Order topics" className="flex w-fit rounded-pill border border-bw-blue-muted/40 p-0.5">
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          aria-pressed={mode === o.value}
          onClick={() => onChange(o.value)}
          className={cn(
            "rounded-pill px-3 py-1 font-sans text-xs font-semibold focus:outline-none focus-visible:ring-2 focus-visible:ring-bw-blue",
            mode === o.value ? "bg-bw-blue/10 text-bw-navy" : "text-bw-ink-muted hover:text-bw-navy",
          )}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

/**
 * The whole catalog as one list in the person's own order: the recommended
 * set first (done ones drop to its end, so the top is always the next one to
 * sit), then the rest of a commit ranking past the cap, then everything
 * unranked in catalog order. The `default`
 * track is the set served to anyone the roster doesn't name, so it is worded
 * as a starting point, not as their own list.
 */
function RecommendedList({
  topics,
  positionsByTopic,
  isDefault,
}: {
  topics: TopicCard[];
  positionsByTopic?: Record<string, number>;
  isDefault: boolean;
}) {
  const byRank = (a: TopicCard, b: TopicCard) => (a.recommended_rank ?? 0) - (b.recommended_rank ?? 0);
  const doneLast = (list: TopicCard[]) => [
    ...list.filter((t) => t.status !== "done").sort(byRank),
    ...list.filter((t) => t.status === "done").sort(byRank),
  ];
  const recommended = doneLast(topics.filter(isRecommended));
  const beyondCap = doneLast(topics.filter((t) => t.recommended_rank != null && !isRecommended(t)));
  const rest = topics.filter((t) => t.recommended_rank == null);
  const sections = [
    { key: "ranked", title: isDefault ? "Start here" : "Your topics, in order", topics: recommended },
    { key: "more", title: "Rest of your ranking", topics: beyondCap },
    { key: "rest", title: "Everything else", topics: rest },
  ].filter((s) => s.topics.length > 0);
  return (
    <div className="flex flex-col gap-8">
      {sections.map((s) => (
        <section key={s.key} id={`recommended-${s.key}`} className="flex flex-col gap-4">
          <h2 className="font-sans text-sm font-semibold uppercase tracking-wide text-bw-ink-muted">{s.title}</h2>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {s.topics.map((t) => (
              <TopicCardView
                key={t.key}
                topic={t}
                positionCount={positionsByTopic ? (positionsByTopic[t.key] ?? 0) : undefined}
              />
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}

/**
 * Surveys — the agentic interview selection page. The left rail shows the
 * full 15-group information architecture and jumps the main pane to a group on
 * click; the main area lists every authored topic. A toggle swaps that for
 * one list in the person's own ranking, when they have one.
 */
export function PreferencesPage() {
  const { data, isLoading, isError, error } = useTopics();
  const opinions = useOpinions();
  const [sortMode, setSortMode] = useState<SortMode>(readSortMode);

  const groups = data ?? [];
  const activeGroups = groups.filter((g) => g.topics.length > 0);
  const allTopics = activeGroups.flatMap((g) => g.topics);
  const doneCount = allTopics.filter((t) => t.status === "done").length;
  const recommendedTopics = allTopics.filter(isRecommended);
  const hasRanking = recommendedTopics.length > 0;
  const recommendedDoneCount = recommendedTopics.filter((t) => t.status === "done").length;
  // One track per person in practice, as in the strip.
  const isDefaultTrack = recommendedTopics[0]?.recommendation_track === "default";
  const mode: SortMode = hasRanking ? sortMode : "group";
  const changeSort = (m: SortMode) => {
    setSortMode(m);
    writeSortMode(m);
  };
  // Positions per topic — undefined while opinions are loading/unavailable so
  // the card omits the pill rather than claiming "not set" it can't know.
  const positionsByTopic = opinions.data
    ? opinions.data.reduce<Record<string, number>>((acc, p) => {
        acc[p.topic_key] = (acc[p.topic_key] ?? 0) + 1;
        return acc;
      }, {})
    : undefined;

  return (
    <div className="flex flex-col gap-8">
      <section className="flex flex-col gap-3">
        <h1 className="text-4xl font-bold text-bw-navy">Surveys</h1>
        {allTopics.length > 0 && (
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="font-sans text-sm font-semibold text-bw-navy">
              {!hasRanking
                ? `${doneCount} of ${allTopics.length} topics done`
                : isDefaultTrack
                  ? `${recommendedDoneCount} of ${recommendedTopics.length} starter topics completed`
                  : `${recommendedDoneCount} of your ${recommendedTopics.length} recommended topics completed`}
            </p>
            {hasRanking && <SortToggle mode={mode} onChange={changeSort} isDefault={isDefaultTrack} />}
          </div>
        )}
      </section>

      {isLoading && <p className="font-sans text-sm text-bw-ink-muted">Loading topics…</p>}

      {isError && (
        <div className="flex items-center gap-2 rounded-card border border-bw-blue-muted/40 p-4 font-sans text-sm text-bw-ink-muted">
          <AlertCircle className="h-4 w-4 shrink-0 text-bw-blue" aria-hidden />
          {Number(error?.status) === 503
            ? "Surveys aren't configured in this environment yet."
            : "Couldn't load topics. Try again shortly."}
        </div>
      )}

      {/* The strip previews the ranking; the recommended view *is* the ranking. */}
      {data && mode === "group" && <RecommendedStrip topics={allTopics} />}

      {data && mode === "recommended" && (
        <RecommendedList topics={allTopics} positionsByTopic={positionsByTopic} isDefault={isDefaultTrack} />
      )}

      {data && mode === "group" && (
        <div className="grid gap-8 lg:grid-cols-[220px_1fr]">
          {/* Left rail: the full IA. Live groups jump the main pane to their
              section; groups without authored topics stay inert placeholders. */}
          <aside className="flex flex-col gap-1 self-start lg:sticky lg:top-6">
            <h2 className="mb-2 font-sans text-xs font-semibold uppercase tracking-wide text-bw-ink-muted">
              Groups
            </h2>
            {groups.map((g) => {
              const count = g.topics.reduce((n, t) => n + t.decision_point_count, 0);
              const live = g.topics.length > 0;
              if (!live) {
                return (
                  <div
                    key={g.key}
                    className="flex items-center justify-between gap-2 rounded-submenu px-2 py-1 font-sans text-sm text-bw-ink-muted/60"
                  >
                    <span>{g.title}</span>
                  </div>
                );
              }
              return (
                <button
                  key={g.key}
                  type="button"
                  onClick={() =>
                    document
                      .getElementById(`group-${g.key}`)
                      ?.scrollIntoView({ behavior: "smooth", block: "start" })
                  }
                  className={cn(
                    "flex items-center justify-between gap-2 rounded-submenu px-2 py-1 text-left font-sans text-sm",
                    "text-bw-navy hover:bg-bw-blue-muted/15 focus:outline-none focus-visible:ring-2 focus-visible:ring-bw-blue",
                  )}
                >
                  <span>{g.title}</span>
                  <span className="rounded-pill bg-bw-blue-muted/15 px-2 py-0.5 text-xs">{count}</span>
                </button>
              );
            })}
          </aside>

          <div className="flex flex-col gap-8">
            {activeGroups.map((g) => (
              <section key={g.key} id={`group-${g.key}`} className="flex scroll-mt-6 flex-col gap-4">
                <h2 className="font-sans text-sm font-semibold uppercase tracking-wide text-bw-ink-muted">
                  {g.title}
                </h2>
                <div className="grid gap-6 sm:grid-cols-2">
                  {g.topics.map((t) => (
                    <TopicCardView
                      key={t.key}
                      topic={t}
                      positionCount={positionsByTopic ? (positionsByTopic[t.key] ?? 0) : undefined}
                    />
                  ))}
                </div>
              </section>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
